import Alert from '~/common/components/Alerts/Alert';
import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import Spinner from '~/common/components/GlobalLayout/Spinner';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';
import useCalculationData from '~/pages/IncomeCalculator/Pages/GenericReviewBreakdown/useCalculationData';
import QualifyingCalculationBreakdown from '~/pages/IncomeCalculator/Pages/ReviewBreakdown/QualifyingCalculation/QualifyingCalculationBreakdown';
import { useIncomeIdentifier } from '~/pages/IncomeCalculator/hooks';
import lang from '~/pages/IncomeCalculator/language.json';

import CalculationHistoryError from './components/CalculationHistoryError';

type PreviousEmploymentBreakdownProps = {
  calculationId: number;
};

export default function PreviousEmploymentBreakdown({
  calculationId,
}: Readonly<PreviousEmploymentBreakdownProps>) {
  const { incomeIdentifier } = useIncomeIdentifier();

  const isEnabled = incomeIdentifier && calculationId > 0;

  const { data, isFetching, isError } = useCalculationData(
    incomeIdentifier,
    calculationId,
    isEnabled,
  );

  if (isError) {
    return <CalculationHistoryError />;
  }

  if (isFetching || !data) {
    return <Spinner />;
  }

  const { initialFormValues, screenData } = data;
  const selectedCalculationData = screenData?.calculationData?.find(
    (calculation) => calculation.calculationId === calculationId,
  );

  const alerts = selectedCalculationData?.alerts;
  const calculatedTotal = selectedCalculationData?.total;
  const { override: isOverriden, overrideAmount } =
    initialFormValues?.calculationDetails[`N-${calculationId}`]?.outcomeDetails
      .base || {};

  return (
    <Flex height="100%" flexDirection="column">
      <Box sx={{ overflowY: 'scroll' }} flexBasis="calc(100% - 36px)">
        {selectedCalculationData && (
          <Box mb={4}>
            <QualifyingCalculationBreakdown
              selectedResult={selectedCalculationData}
              incluldeAlerts={true}
              fullWidth={true}
            />
          </Box>
        )}

        <Text mb={4} data-testid="calculated-income">
          <Text as="span" fontWeight={500}>
            {lang.CALCULATED_MONTHLY_INCOME}&nbsp;
          </Text>
          {Number.isNaN(calculatedTotal)
            ? 'N/A'
            : formatCurrency(calculatedTotal)}
        </Text>
        {isOverriden && (
          <Text mb={4} data-testid="overridden-income">
            <Text as="span" fontWeight={500}>
              {lang.OVERRIDDEN_MONTHLY_INCOME}&nbsp;
            </Text>
            {Number.isNaN(overrideAmount)
              ? 'N/A'
              : formatCurrency(overrideAmount)}
          </Text>
        )}

        {alerts && (
          <Stack spacing={3} mt={8} my={4}>
            {alerts.map((alert) => (
              <Alert
                title={alert.title}
                status={alert.severity}
                key={alert.description}
              >
                {alert.description}
              </Alert>
            ))}
          </Stack>
        )}

        {initialFormValues?.comments && (
          <Box>
            <Text fontWeight={500} borderBottom="2px solid" mb={4}>
              {lang.COMMENT}
            </Text>
            <Text>{initialFormValues.comments}</Text>
          </Box>
        )}
      </Box>
    </Flex>
  );
}
